import {
  Bug,
  CheckCircle2,
  Lightbulb,
  Loader2,
  MessageSquarePlus,
  RefreshCw,
  Send,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DuoSelect from "src/components/DuoSelect";
import { Constants } from "src/constant";
import {
  FEEDBACK_SERVICE_OPTIONS,
  fetchFeedback,
  getFeedbackServiceLabel,
  reporterFromAuth,
  submitFeedback,
  type FeedbackListItem,
  type FeedbackService,
  type FeedbackType,
} from "src/lib/feedbackService";
import { hasPermission, Permission } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";

const TITLE_MAX = 140;
const DESCRIPTION_MAX = 4000;

const TYPE_META: Record<
  FeedbackType,
  { label: string; icon: typeof Bug; badge: string }
> = {
  bug: {
    label: "Bug",
    icon: Bug,
    badge: "border-red-400/30 bg-red-500/15 text-red-200",
  },
  feature: {
    label: "Feature",
    icon: Lightbulb,
    badge: "border-amber-400/30 bg-amber-500/15 text-amber-200",
  },
};

const serviceOptions = FEEDBACK_SERVICE_OPTIONS.map((service) => ({
  value: service,
  label: getFeedbackServiceLabel(service),
}));

interface SettingsFeedbackSectionProps {
  isActive?: boolean;
  refreshKey?: number;
}

const SettingsFeedbackSection: React.FC<SettingsFeedbackSectionProps> = ({
  isActive = false,
  refreshKey = 0,
}) => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);

  const canViewFeedback =
    userProfile != null &&
    hasPermission(userProfile.role, Permission.VIEW_FEEDBACK);

  // ── Submission form ──────────────────────────────────────────────
  const [type, setType] = useState<FeedbackType>("bug");
  const [service, setService] = useState<FeedbackService>("general");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const reporter = useMemo(
    () => reporterFromAuth(user, userProfile),
    [user, userProfile],
  );

  const canSubmit =
    !submitting &&
    reporter != null &&
    title.trim() !== "" &&
    description.trim() !== "";

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reporter || submitting) return;

      setSubmitting(true);
      setSubmitError(null);
      setSubmitted(false);

      try {
        await submitFeedback({ type, service, title, description }, reporter);
        setSubmitted(true);
        setTitle("");
        setDescription("");
        setService("general");
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : "Could not submit your report.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [reporter, submitting, type, service, title, description],
  );

  // ── Admin reports feed ───────────────────────────────────────────
  const [reports, setReports] = useState<FeedbackListItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<"all" | FeedbackType>("all");

  const loadReports = useCallback(async () => {
    if (!canViewFeedback) return;

    setFeedLoading(true);
    setFeedError(null);
    try {
      setReports(await fetchFeedback());
    } catch (err) {
      setFeedError(
        err instanceof Error ? err.message : "Failed to load feedback.",
      );
    } finally {
      setFeedLoading(false);
    }
  }, [canViewFeedback]);

  useEffect(() => {
    if (!isActive || !canViewFeedback) return;
    void loadReports();
  }, [isActive, canViewFeedback, refreshKey, loadReports]);

  const filteredReports = useMemo(
    () => reports.filter((r) => typeFilter === "all" || r.type === typeFilter),
    [reports, typeFilter],
  );

  return (
    <section className="flex h-full min-h-0 flex-col gap-3">
      <header className="shrink-0 rounded-2xl border border-white/10 bg-gray-950/90 p-4 shadow-sm backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5 text-gray-300" aria-hidden />
          <h2 className="text-lg font-extrabold tracking-tight text-white">
            Feedback
          </h2>
        </div>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-gray-400">
          Found a bug or have an idea? Send it to the team. Reports are private
          — only administrators can review them.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
        {/* ── Submission form ── */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-white/10 bg-gray-950/50 p-4 sm:p-5"
        >
          <fieldset className="mb-4">
            <legend className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">
              What would you like to send?
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(TYPE_META) as FeedbackType[]).map((key) => {
                const meta = TYPE_META[key];
                const Icon = meta.icon;
                const selected = type === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setType(key)}
                    aria-pressed={selected}
                    className={[
                      "inline-flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5",
                      "text-sm font-extrabold transition",
                      "focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20",
                      selected
                        ? "border-white/20 bg-white/10 text-white shadow-[0_4px_0_rgba(255,255,255,0.15)]"
                        : "border-white/10 bg-white/[0.03] text-gray-300 hover:border-white/20 hover:bg-white/10 hover:text-white",
                    ].join(" ")}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {key === "bug" ? "Report a bug" : "Request a feature"}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <label className="mb-4 block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-400">
              {type === "bug" ? "Which area is affected?" : "Related area"}
            </span>
            <DuoSelect
              value={service}
              onChange={setService}
              options={serviceOptions}
              label="Area"
              className="w-full border-white/10 bg-gray-150 sm:w-72"
            />
          </label>

          <label className="mb-4 block">
            <span className="mb-1.5 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-gray-400">
              Title
              <span className="font-medium normal-case text-gray-600">
                {title.length}/{TITLE_MAX}
              </span>
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              maxLength={TITLE_MAX}
              placeholder={
                type === "bug"
                  ? "e.g. Broadcast preview shows the wrong icon"
                  : "e.g. Bulk-archive feature flags"
              }
              className="w-full rounded-xl border border-white/10 bg-gray-950/60 px-3 py-2.5 text-sm text-white placeholder:text-gray-600 focus:border-white/25 focus:outline-none focus:ring-2 focus:ring-white/10"
            />
          </label>

          <label className="mb-4 block">
            <span className="mb-1.5 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-gray-400">
              Description
              <span className="font-medium normal-case text-gray-600">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </span>
            <textarea
              value={description}
              onChange={(e) =>
                setDescription(e.target.value.slice(0, DESCRIPTION_MAX))
              }
              maxLength={DESCRIPTION_MAX}
              rows={5}
              placeholder={
                type === "bug"
                  ? "What happened, what you expected, and steps to reproduce."
                  : "What problem would this solve, and how do you imagine it working?"
              }
              className="w-full resize-y rounded-xl border border-white/10 bg-gray-950/60 px-3 py-2.5 text-sm leading-6 text-white placeholder:text-gray-600 focus:border-white/25 focus:outline-none focus:ring-2 focus:ring-white/10"
            />
          </label>

          {submitError && (
            <div
              className="mb-3 rounded-xl border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-100"
              role="alert"
            >
              {submitError}
            </div>
          )}

          {submitted && (
            <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-100">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
              Thanks! Your report was sent to the team.
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/15 px-4 text-sm font-extrabold text-emerald-100 transition hover:bg-emerald-400/25 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
            {submitting ? "Sending…" : "Send report"}
          </button>
        </form>

        {/* ── Admin-only reports list ── */}
        {canViewFeedback && (
          <div className="mt-5">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-gray-300">
                All reports
                {reports.length > 0 && (
                  <span className="ml-2 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-bold text-gray-400">
                    {filteredReports.length}
                  </span>
                )}
              </h3>

              <div className="flex items-center gap-2">
                <DuoSelect
                  value={typeFilter}
                  onChange={setTypeFilter}
                  options={[
                    { value: "all", label: "All types" },
                    { value: "bug", label: "Bugs" },
                    { value: "feature", label: "Features" },
                  ]}
                  label="Type"
                  className="w-36 border-white/10 bg-gray-150"
                />
                <button
                  type="button"
                  onClick={() => void loadReports()}
                  disabled={feedLoading}
                  aria-label="Refresh reports"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${feedLoading ? "animate-spin" : ""}`}
                    aria-hidden
                  />
                </button>
              </div>
            </div>

            {feedError && (
              <div
                className="mb-3 rounded-xl border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-100"
                role="alert"
              >
                {feedError}
              </div>
            )}

            {feedLoading && reports.length === 0 ? (
              <div className="flex min-h-[10rem] items-center justify-center gap-3 text-sm text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                Loading reports…
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-10 text-center text-sm text-gray-400">
                {reports.length === 0
                  ? "No reports submitted yet."
                  : "No reports match these filters."}
              </div>
            ) : (
              <ul className="space-y-2">
                {filteredReports.map((report) => {
                  const meta = TYPE_META[report.type];
                  const Icon = meta.icon;
                  return (
                    <li
                      key={report.id}
                      className="rounded-2xl border border-white/10 bg-gray-950/50 p-4"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[0.7rem] font-extrabold uppercase ${meta.badge}`}
                        >
                          <Icon className="h-3 w-3" aria-hidden />
                          {meta.label}
                        </span>
                        <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-0.5 text-[0.7rem] font-bold text-gray-300">
                          {getFeedbackServiceLabel(report.service)}
                        </span>
                        <span className="ml-auto text-xs text-gray-500">
                          {report.createdAtMs
                            ? new Date(report.createdAtMs).toLocaleString()
                            : "Just now"}
                        </span>
                      </div>

                      <p className="mt-2 text-sm font-extrabold text-white">
                        {report.title}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-gray-300">
                        {report.description}
                      </p>

                      <div className="mt-3 border-t border-white/5 pt-3">
                        <span className="text-xs text-gray-500">
                          {report.reporter?.displayName
                            ? `${report.reporter.displayName} · `
                            : ""}
                          {report.reporter?.email ?? "Unknown reporter"}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="mt-3 text-center text-xs text-gray-500">
              Reports are automatically deleted{" "}
              {Constants.FEEDBACK_RETENTION_DAYS} days after they're submitted.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default SettingsFeedbackSection;
